/**
 * Numeric design tokens shared across every Rynora React app.
 *
 * Colour, spacing, typography, radii, and shadows live in
 * `tokens.css` as CSS custom properties so they stay
 * theme-aware. Constants below are the values that need to be
 * passed to JS APIs (image dimensions, modal widths, etc.) and
 * therefore can't live as CSS variables.
 */

/** Lucide icon sizes — pick the bucket that matches your surface density. */
export const ICON_SIZE = {
  /** Inline tree rows, dense lists, compact toolbars. */
  xs: 12,
  /** Standard toolbar buttons, modal close, settings rows. */
  sm: 14,
  /** Section headers, form-field affordances, card titles. */
  md: 16,
  /** Modal headers, page-level icons. */
  lg: 18,
  /** Empty-state, hero, marketing. */
  xl: 24,
  /** Welcome / onboarding / large feature illustrations. */
  hero: 40,
} as const;

export type IconSize = keyof typeof ICON_SIZE;

/**
 * Standard interactive control heights, in CSS pixels.
 * Keep in sync with `--control-height-*` in app stylesheets
 * (`styles-ui-consistency.css` in flow-builder, `@rynora/ui` primitives).
 */
export const CONTROL_HEIGHT = {
  /** Dense toolbars, compact forms, table row actions. */
  sm: 32,
  /** Default buttons, inputs, and selects. */
  md: 36,
  /** Primary CTAs and touch-friendly targets. */
  lg: 40,
} as const;

export type ControlHeight = keyof typeof CONTROL_HEIGHT;

/**
 * Brand mark dimensions, in CSS pixels.
 *
 * History (May 2026, in order):
 *   1. Original three sizes (24 / 32 / 56) per variant, paired with
 *      smaller wordmark fonts (16 / 18 / 24). The numeric ratios
 *      (logo 1.5x–2.3x the wordmark font) made the logo image
 *      dwarf the "Rynora" text in every lockup — readers saw a big
 *      icon next to small text rather than a single, balanced unit.
 *   2. Collapsed everything to a single 32px logo + 20px wordmark
 *      ("regular everywhere") to remove the cross-surface jump.
 *      Still 1.6x mismatched; tester flagged that the logo height
 *      was visibly taller than the "Rynora" text height.
 *   3. Restored per-variant differentiation AND locked the logo
 *      height to the wordmark font-size (1:1) in each variant, so
 *      the logo's bounding box matches the wordmark's em-box and
 *      the lockup reads as one piece on every surface:
 *
 *        compact 24/24/14 — sidebar, dashboard header, dense toolbars
 *        regular 32/32/14 — login, invite-accept, page headers
 *        hero    56/56/18 — welcome wizard, splash, full-screen onboarding
 *
 * The matching wordmark font-sizes live in tokens.css under the
 * `.brand-mark--<variant> .brand-mark__wordmark` rules; keep this
 * map and those rules in sync if you ever retune a variant.
 */
export const BRAND_LOGO_SIZE = {
  /** Sidebar, dashboard header, help sidebar, dense toolbars. Pairs with --font-2xl (24px) wordmark. */
  compact: 24,
  /** Login marketing column, invite acceptance card, page headers. Pairs with --font-3xl (32px) wordmark. */
  regular: 32,
  /** Welcome wizard, onboarding, splash. Pairs with --font-4xl (56px) wordmark. */
  hero: 56,
} as const;

export type BrandLogoSize = keyof typeof BRAND_LOGO_SIZE;
